/**
 * InvitePage - Trust-chain invite landing page
 * 
 * A rite of entry, not a landing page.
 * 
 * Flow:
 * 1. User opens /invite/:code
 * 2. Page resolves invite (public call)
 * 3. If not logged in: "Accept Invite" saves code and redirects to /auth
 * 4. If logged in: saves code and redirects to /connections (App.js handles consumption)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ConnectionsAPI } from "../api/connectionsApi";
import { useAuth } from "../context/AuthContext";
import { PageShell } from "../components/layout/PageShell";

const modeLabel = (m) => {
  const map = {
    friendship: "Friendship",
    business: "Business",
    community: "Community",
    dating: "Dating",
  };
  return map[m] || "Friendship";
};

const modeEmoji = (m) => {
  const map = {
    friendship: "🤝",
    business: "💼",
    community: "🌍",
    dating: "❤️",
  };
  return map[m] || "🤝";
};

export default function InvitePage() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const hasRedirected = useRef(false);

  const safeCode = useMemo(() => (code || "").trim(), [code]);

  // Resolve invite on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      setErr("");
      setInvite(null);
      if (!safeCode) return;
      try {
        const res = await ConnectionsAPI.resolveInvite(safeCode);
        if (!mounted) return;
        setInvite(res);
      } catch (e) {
        if (!mounted) return;
        setErr(e?.message || "Invite link not valid.");
      }
    })();
    return () => {
      mounted = false;
    };
  }, [safeCode]);

  // If logged in AND we have a valid invite, redirect to connections
  // The actual consumption happens in App.js via the pending_invite_code effect
  // This prevents double-consume calls
  useEffect(() => {
    if (!user) return;
    if (!invite?.code) return;
    if (hasRedirected.current) return;

    // User is already logged in - save code and let App.js handle consumption
    hasRedirected.current = true;
    localStorage.setItem("pending_invite_code", invite.code);
    nav("/connections");
  }, [user, invite, nav]);

  const handleAccept = () => {
    // Save code so after auth the app can consume it
    localStorage.setItem("pending_invite_code", safeCode);
    nav("/auth");
  };

  return (
    <PageShell className="flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="rounded-2xl surface-card p-8 shadow-xl">
          
          {/* Header - Emotional Anchor */}
          <div className="text-center mb-8">
            <div className="text-4xl mb-4">💌</div>
            <h1 className="text-2xl font-semibold text-slate-800 dark:text-slate-100 tracking-tight">
              You were invited — <span className="text-rose-600 dark:text-rose-400">intentionally</span>
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 leading-relaxed">
              Unity Love uses private invite links to keep connections respectful, focused, and human.
            </p>
          </div>

          {/* Loading state */}
          {!invite && !err && (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 dark:border-slate-700 border-t-rose-500 mx-auto"></div>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-4">Loading invite…</p>
            </div>
          )}

          {/* Error state */}
          {err && (
            <div className="rounded-xl border border-red-100 dark:border-red-900/50 bg-red-50/50 dark:bg-red-900/20 p-5 text-center">
              <div className="text-2xl mb-2">😔</div>
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">This invite is no longer valid</p>
              <p className="text-xs text-red-500/80 dark:text-red-400/70 mt-1">{err}</p>
            </div>
          )}

          {/* Invite details */}
          {invite && (
            <div className="space-y-6">
              
              {/* Invite Context Card */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 p-5">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full surface-card flex items-center justify-center text-2xl shadow-sm">
                    {modeEmoji(invite.mode)}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800 dark:text-slate-100">
                      {modeLabel(invite.mode)} Connection Invite
                    </div>
                    <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                      Created by a trusted member
                    </div>
                  </div>
                </div>
                
                {/* Note - The most human part */}
                {invite.note && (
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="flex items-start gap-2">
                      <span className="text-slate-400 dark:text-slate-500 text-sm">💬</span>
                      <p className="text-sm text-slate-600 dark:text-slate-300 italic leading-relaxed">
                        &ldquo;{invite.note}&rdquo;
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* What happens next - Clarity = Comfort */}
              <div className="text-xs text-slate-400 dark:text-slate-500 space-y-1.5">
                <div className="font-medium text-slate-500 dark:text-slate-400 mb-2">What happens when you continue:</div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-300 dark:text-slate-600">•</span>
                  <span>You&apos;ll create or log into your account</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-300 dark:text-slate-600">•</span>
                  <span>This connection will appear under Connections</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-300 dark:text-slate-600">•</span>
                  <span>You stay in control of visibility and next steps</span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-3 pt-2">
                {!user ? (
                  <button
                    className="w-full rounded-xl px-5 py-3.5 bg-slate-800 dark:bg-slate-700 text-white font-medium hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 shadow-sm"
                    onClick={handleAccept}
                    disabled={!invite}
                  >
                    Accept Invite
                  </button>
                ) : (
                  <button
                    className="w-full rounded-xl px-5 py-3.5 bg-slate-800 dark:bg-slate-700 text-white font-medium disabled:opacity-50"
                    disabled
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></span>
                      Applying invite…
                    </span>
                  </button>
                )}

                <button
                  className="w-full rounded-xl px-4 py-2.5 text-sm text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  onClick={() => nav("/")}
                >
                  Go Home
                </button>
              </div>

              {user && (
                <p className="text-xs text-center text-slate-400 dark:text-slate-500">
                  You&apos;re logged in — applying invite automatically…
                </p>
              )}
            </div>
          )}

          {/* Footer - Refined reassurance */}
          <div className="mt-8 pt-5 border-t border-slate-100 dark:border-slate-800 text-center">
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Built for intentional connections — not public feeds.
            </p>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
